import { useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import CountUp from "react-countup";
import { Filter, MapPin, Zap, Clock } from "lucide-react";

const projects = [
  { client: "Victura Group", capacity: "6,000 kW", capacityNum: 6000, location: "Pan India", model: "\u2014", sector: "Auto" },
  { client: "Amber", capacity: "5,000 kW", capacityNum: 5000, location: "Pan India", model: "\u2014", sector: "Auto" },
  { client: "Imperial Auto", capacity: "2,650 kW", capacityNum: 2650, location: "Bawal, HR", model: "\u2014", sector: "Auto" },
  { client: "ACCIL", capacity: "2,600 kW", capacityNum: 2600, location: "3 Sites, HR & RJ", model: "CapEx, 5 Yr Loan", sector: "Auto" },
  { client: "Vee Gee Industries", capacity: "2,500 kW", capacityNum: 2500, location: "Faridabad HR & GJ", model: "20 Yr PPA", sector: "Auto" },
  { client: "Lion Fabrics", capacity: "2,000 kW", capacityNum: 2000, location: "Bhopal, MP", model: "25 Yr PPA", sector: "Textile" },
  { client: "Premier Indoplast", capacity: "1,500 kW", capacityNum: 1500, location: "Tapukara RJ, Bengaluru KA, Faridabad HR", model: "\u2014", sector: "Manufacturing" },
  { client: "Lemon Tree Hotels", capacity: "1,500 kW", capacityNum: 1500, location: "Pan India", model: "25 Yr PPA", sector: "Hospitality" },
  { client: "Victora Industries", capacity: "1,500 kW", capacityNum: 1500, location: "Faridabad & Becharji", model: "PPA", sector: "Auto" },
  { client: "F-Tech Inc. (Japan)", capacity: "600 kWp", capacityNum: 600, location: "Greater Noida, UP", model: "25 Yr PPA", sector: "Auto" },
  { client: "Sona Beverages", capacity: "500 kW", capacityNum: 500, location: "Durg, Chhattisgarh", model: "25 Yr PPA", sector: "F&B" },
  { client: "Anya Polytech & Fertilizers", capacity: "500 kW", capacityNum: 500, location: "Shahjahanpur, UP", model: "20 Yr PPA", sector: "Manufacturing" },
  { client: "Omniplast", capacity: "500 kW", capacityNum: 500, location: "Ghelot, RJ", model: "20 Yr PPA", sector: "Manufacturing" },
  { client: "Orient Kagaz Converters", capacity: "300 kW", capacityNum: 300, location: "Bhopal, MP", model: "CapEx", sector: "Packaging" },
  { client: "NKM Cables", capacity: "300 kW", capacityNum: 300, location: "Bhopal, MP", model: "CapEx", sector: "Manufacturing" },
  { client: "Spectrum Resort", capacity: "300 kW", capacityNum: 300, location: "Udaipur, RJ", model: "20 Yr PPA", sector: "Hospitality" },
  { client: "CMI Limited", capacity: "200 kW", capacityNum: 200, location: "Faridabad & Bawal, HR", model: "25 Yr PPA", sector: "Manufacturing" },
  { client: "Shivmani Exports", capacity: "200 kW", capacityNum: 200, location: "Kolkata, WB", model: "25 Yr PPA", sector: "Textile" },
];

const sectorFilters = ["All", "Auto", "Manufacturing", "Textile", "Hospitality", "F&B", "Packaging"];

export default function ProjectsPage() {
  const [filter, setFilter] = useState("All");
  const statsRef = useRef(null);
  const statsInView = useInView(statsRef, { once: true });

  const filtered =
    filter === "All"
      ? projects
      : projects.filter((p) => p.sector === filter);

  return (
    <div>
      {/* ── Hero ─────────────────────────────────────────── */}
      <section className="relative pt-32 pb-24 bg-[#0A1628]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#00D68F] mb-4">
              Our Work
            </p>
            <h1 className="text-5xl md:text-6xl font-black tracking-tighter text-white">
              Project <span className="text-[#00D68F]">Portfolio</span>
            </h1>
          </motion.div>
        </div>
      </section>

      {/* ── Stats ────────────────────────────────────────── */}
      <section
        ref={statsRef}
        className="bg-white py-16 border-b border-gray-100"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { val: 30, suffix: " MW+", label: "Installed" },
            { val: 18, suffix: "+", label: "Active Clients" },
            { val: 10, suffix: "+", label: "States" },
            { val: 32000, suffix: "+", label: "Tonnes CO\u2082 Reduced/Yr" },
          ].map((s, i) => (
            <div key={i} className="text-center" data-testid={`portfolio-stat-${i}`}>
              <div className="text-4xl font-black text-[#00D68F]">
                {statsInView ? (
                  <CountUp end={s.val} duration={2} separator="," />
                ) : (
                  0
                )}
                {s.suffix}
              </div>
              <div className="text-[#0A1628]/60 text-sm mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Filter & Grid ────────────────────────────────── */}
      <section className="py-24 bg-white" data-testid="projects-grid-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3 mb-12 flex-wrap">
            <Filter size={16} className="text-[#0A1628]/40" />
            {sectorFilters.map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                data-testid={`filter-${s.toLowerCase()}`}
                className={`px-4 py-2 text-sm font-bold transition-all ${
                  filter === s
                    ? "bg-[#0A1628] text-white"
                    : "bg-[#F5F7FA] text-[#0A1628]/60 hover:bg-[#0A1628]/10"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((p, i) => (
              <motion.div
                key={p.client}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="bg-white border border-gray-200 p-6 hover:shadow-xl hover:-translate-y-1 transition-all"
                data-testid={`project-card-${i}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-lg font-bold text-[#0A1628]">
                    {p.client}
                  </h3>
                  <span className="text-xs font-bold bg-[#00D68F]/10 text-[#00D68F] px-2 py-1 shrink-0 ml-2">
                    {p.sector}
                  </span>
                </div>
                <div className="space-y-2 text-sm text-[#0A1628]/60">
                  <div className="flex items-center gap-2">
                    <Zap size={14} className="text-[#00D68F]" /> {p.capacity}
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin size={14} className="text-[#00D68F]" />{" "}
                    {p.location}
                  </div>
                  {p.model !== "\u2014" && (
                    <div className="flex items-center gap-2">
                      <Clock size={14} className="text-[#00D68F]" /> {p.model}
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Auto Sector Spotlight ─────────────────────────── */}
      <section className="py-24 md:py-32 bg-[#0A1628]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#00D68F] mb-4">
            Case Study
          </p>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-white mb-6">
            Auto Sector Spotlight
          </h2>
          <p className="text-white/60 text-base md:text-lg max-w-3xl mb-12">
            Over 30 MW installed in the automotive sector alone. Our clients
            include component suppliers to Mercedes, Mahindra, Maruti, and
            Toyota. India Steel Summit became F-Tech Group Japan's first
            solar-powered facility.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { label: "Auto Clients", val: "7+" },
              { label: "Total Capacity", val: "30 MW+" },
              { label: "CO\u2082 Reduced/Yr", val: "3,000+ t" },
              { label: "First Solar-Powered", val: "F-Tech Japan" },
            ].map((s, i) => (
              <div
                key={i}
                className="bg-white/5 backdrop-blur-xl border border-white/10 p-6 text-center"
              >
                <div className="text-2xl font-black text-[#00D68F]">
                  {s.val}
                </div>
                <div className="text-white/50 text-xs mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
